import { useEffect, useRef, useState } from 'react';
import { member } from '../lib/api';
import type { UserProfile as UserProfileType } from '../types';
import { useAuth } from '../auth/AuthProvider';
import { Avatar } from '../ui/avatar';
import { Button } from '../ui/button';
import { ErrorMessage, Field, Label } from '../ui/fieldset';
import { Heading } from '../ui/heading';
import { Input } from '../ui/input';
import { Text } from '../ui/text';
import { Panel } from '../components/PageChrome';

interface FormState {
  firstName: string;
  lastName: string;
  displayName: string;
  avatarPreviewUrl: string;
  phone: string;
  city: string;
  state: string;
  timezone: string;
}

const fromProfile = (profile: UserProfileType): FormState => ({
  firstName: profile.firstName ?? '',
  lastName: profile.lastName ?? '',
  displayName: profile.displayName ?? '',
  avatarPreviewUrl: profile.avatarUrl ?? '',
  phone: profile.phone ?? '',
  city: profile.city ?? '',
  state: profile.state ?? '',
  timezone: profile.timezone ?? '',
});

const nullable = (value: string) => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const fullName = (firstName: string | undefined, lastName: string | undefined) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const initialsFor = (
  displayName: string | undefined,
  firstName: string | undefined,
  lastName: string | undefined,
  email: string | undefined,
) => {
  const source =
    displayName?.trim() || fullName(firstName, lastName) || email?.split('@')[0] || '?';
  return source
    .split(/[._\-\s]+/)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2);
};

interface UserProfileProps {
  backHref?: string;
  backLabel?: string;
  signInFrom?: string;
}

export default function UserProfile({
  backHref = '/me',
  backLabel = 'My hub',
  signInFrom = '/me/profile',
}: UserProfileProps = {}) {
  const { user, email } = useAuth();
  const [profile, setProfile] = useState<UserProfileType>();
  const [form, setForm] = useState<FormState>();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>();
  const [savedAt, setSavedAt] = useState<string>();
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    const next = await member.profile();
    setProfile(next);
    setForm(fromProfile(next));
  };

  useEffect(() => {
    if (!user) return;
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const uploadAvatar = async (file: File | undefined) => {
    if (!file || !form) return;
    setUploading(true);
    setError(undefined);
    try {
      const signed = await member.createAssetUploadUrl({
        type: 'userAvatar',
        contentType: file.type,
      });
      const res = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status} ${res.statusText}`);
      const next = await member.updateProfile({ avatarKey: signed.key });
      setProfile(next);
      setForm((current) =>
        current ? { ...current, avatarPreviewUrl: next.avatarUrl ?? signed.publicUrl } : fromProfile(next),
      );
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(undefined);
    try {
      const next = await member.updateProfile({
        firstName: nullable(form.firstName),
        lastName: nullable(form.lastName),
        displayName: nullable(form.displayName),
        phone: nullable(form.phone),
        city: nullable(form.city),
        state: nullable(form.state),
        timezone: nullable(form.timezone),
      });
      setProfile(next);
      setForm(fromProfile(next));
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Heading>Profile</Heading>
        <Panel className="text-center">
          <Text>Sign in to manage your BaseballSTRM profile.</Text>
          <Button href={`/login?from=${encodeURIComponent(signInFrom)}`} color="dark/zinc" className="mt-4">
            Sign in
          </Button>
        </Panel>
      </div>
    );
  }

  if (!form || !profile) {
    return (
      <div className="mx-auto max-w-3xl">
        {error ? <Text className="text-red-400">{error}</Text> : <Text>Loading profile...</Text>}
      </div>
    );
  }

  const nameForAlt = form.displayName || fullName(form.firstName, form.lastName);
  const initials = initialsFor(form.displayName, form.firstName, form.lastName, profile.email ?? email);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading>Profile</Heading>
          <Text className="mt-1">Your account photo and contact details across BaseballSTRM.</Text>
        </div>
        <Button href={backHref} outline>
          {backLabel}
        </Button>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Panel className="space-y-5">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <Avatar
              src={form.avatarPreviewUrl || undefined}
              initials={initials}
              square
              alt={nameForAlt || profile.email || 'Profile'}
              className="size-24 text-zinc-400"
            />
            <div className="grid flex-1 gap-4 sm:grid-cols-2">
              <Field className="sm:col-span-2">
                <Label>Profile photo</Label>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    outline
                    disabled={uploading}
                    onClick={() => avatarInputRef.current?.click()}
                  >
                    {uploading ? 'Uploading...' : 'Upload avatar'}
                  </Button>
                  <Text className="text-xs text-zinc-500">PNG, JPG, or WebP</Text>
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={(e) => void uploadAvatar(e.target.files?.[0])}
                  disabled={uploading}
                />
              </Field>
              <Field>
                <Label>First name</Label>
                <Input
                  value={form.firstName}
                  autoComplete="given-name"
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
              </Field>
              <Field>
                <Label>Last name</Label>
                <Input
                  value={form.lastName}
                  autoComplete="family-name"
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </Field>
              <Field>
                <Label>Display name</Label>
                <Input
                  value={form.displayName}
                  autoComplete="nickname"
                  onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                />
              </Field>
              <Field>
                <Label>Email</Label>
                <Input value={profile.email ?? email ?? ''} disabled />
              </Field>
              <Field>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field>
                <Label>Timezone</Label>
                <Input
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  placeholder="America/Boise"
                />
              </Field>
              <Field>
                <Label>City</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </Field>
              <Field>
                <Label>State</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              </Field>
            </div>
          </div>
        </Panel>

        {error ? <ErrorMessage>{error}</ErrorMessage> : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" color="dark/zinc" disabled={saving || uploading}>
            {saving ? 'Saving...' : uploading ? 'Uploading...' : 'Save profile'}
          </Button>
          {savedAt ? <Text className="text-xs text-zinc-500">Saved at {savedAt}</Text> : null}
        </div>
      </form>
    </div>
  );
}
